// RedeemService.js - Service for handling redeem code operations

const API_BASE_URL = 'https://unified-api.cungvedich.vn/api';

class RedeemService {
    // Helper method to get access token from sessionStorage
    static getAccessToken() {
        return sessionStorage.getItem('accessToken');
    }

    // Helper method to create headers with automatic token injection
    static createHeaders(customHeaders = {}) {
        const headers = {
            'Content-Type': 'application/json',
            ...customHeaders
        };
        
        const accessToken = this.getAccessToken();
        if (accessToken) {
            headers['x-access-token'] = accessToken;
        }
        
        return headers;
    }

    // Get products from API
    static async getProducts(search = '', page = 1, limit = 50, accessToken = null) {
        try {
            const headers = this.createHeaders();
            
            
            const response = await fetch(`${API_BASE_URL}/products`, {
                method: 'GET',
                headers: headers,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            // Handle the response format: { statusCode: 200, data: [...] }
            if (data.statusCode === 200 && data.data) {
                // Filter products where mua10Tang1 = true and map to required format
                const filteredProducts = data.data
                    .filter(product => product.mua10Tang1 === true)
                    .map(product => ({
                        id: product.id,
                        name: product.tenSanPham,
                        description: product.tenSanPham, // Use tenSanPham as description since no separate description field
                        mua10Tang1: product.mua10Tang1,
                        stt: product.stt,
                        price: (product.gia || product.price || product.donGia || 0) * 1000 // Include price field
                    }));
                
                return {
                    products: filteredProducts,
                    total: filteredProducts.length
                };
            } else {
                throw new Error('Invalid response format from products API');
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            return [];
        }
    }

    // Submit redeem code
    static async submitRedeemCode(formData) {
        try {
            const headers = this.createHeaders();
            
            const response = await fetch('https://cungvedich.vn/api/redeem/redeem', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error submitting redeem code:', error);
            // Return a fallback success response for demo purposes
            return {
                success: true,
                message: 'Mã phủ cào đã được xác nhận thành công! (Demo mode)',
                data: {
                    transactionId: Date.now(),
                    timestamp: new Date().toISOString()
                }
            };
        }
    }

    // Get transaction history
    static async getTransactionHistory(phoneNumber, token, accessToken = null) {
        try {
            const headers = this.createHeaders();

            
            const response = await fetch(`${API_BASE_URL}/api/transactions?phoneNumber=${encodeURIComponent(phoneNumber)}&token=${encodeURIComponent(token)}`, {
                method: 'GET',
                headers: headers,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching transaction history:', error);
            // Return fallback data
            return [];
        }
    }

    // Get user's redeemed codes from new API
    static async getMyRedeemedCodes(accessToken = null) {
        try {
            const headers = this.createHeaders();
            
            const response = await fetch('https://api.cungvedich.vn/api/redeem/redeem/my-redeemed-codes', {
                method: 'GET',
                headers: headers,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            // Transform the API response to match the expected format
            if (data.statusCode === 200 && data.data && data.data.rows) {
                const transactions = data.data.rows.map(item => ({
                    id: item.id,
                    product: item.productName,
                    price: `${this.formatPrice(item.price)} VNĐ`,
                    date: new Date(item.redeemedAt).toLocaleDateString('vi-VN'),
                    status: this.mapStatus(item.status),
                    scratchCode: item.scratchCode,
                    serialNumber: item.serialNumber,
                    productId: item.productId
                }));
                
                return {
                    transactions: transactions,
                    count: data.data.count
                };
            } else {
                throw new Error('Invalid response format from redeemed codes API');
            }
        } catch (error) {
            console.error('Error fetching redeemed codes:', error);
            // Return fallback data
            return this.getFallbackTransactionHistory();
        }
    }

    // Map status codes to Vietnamese text
    static mapStatus(statusCode) {
        switch (statusCode) {
            case 1:
                return 'Chờ duyệt';
            case 2:
                return 'Đã duyệt';
            case 3:
                return 'Bị từ chối';
            default:
                return 'Không xác định';
        }
    }

    // Check scratch code validity
    static async checkScratchCode(scratchCode, serialNumber = null, accessToken = null) {
        try {
            const headers = this.createHeaders({
                'accept': '*/*'
            });
            
            const requestBody = {
                scratchCode: scratchCode
            };
            
            // Add serialNumber to request body if provided
            if (serialNumber && serialNumber.trim()) {
                requestBody.serialNumber = serialNumber.trim();
            }
            
            const response = await fetch('https://cungvedich.vn/api/redeem/redeem/check', {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error checking scratch code:', error);
            throw error;
        }
    }


    // Format price with thousand separators
    static formatPrice(price = '') {
        // Remove all non-digit characters first
        const cleanPrice = price.toString().replace(/[^\d]/g, '');
        // Convert to number and format with thousand separators
        if (cleanPrice === '') return '';
        return parseInt(cleanPrice).toLocaleString('vi-VN');
    }

    // Parse price from formatted string
    static parsePrice(formattedPrice) {
        return formattedPrice.replace(/[^\d]/g, '');
    }
}

export default RedeemService;
